import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Form, Formik } from "formik";
import MuiPhoneNumber from "material-ui-phone-number";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import LabelsContext from "../../../lang/LabelsContext";
import Labels from "../../../lang/Labels_en";
import UserService, { UserServiceType } from "../../../service/user/UserService";
import Password from "../../system/password/Password";
import useStyles from "./ProfileStyle";

const orcidRegExp = /[0-9][0-9][0-9][0-9]-[0-9][0-9][0-9][0-9]-[0-9][0-9][0-9][0-9]-[0-9][0-9][0-9][0-9]/;

const validationSchema = yup.object().shape({
  firstName: yup.string().required(Labels.FIRSTNAME_REQUIRED),
  lastName: yup.string().required(Labels.LASTNAME_REQUIRED),
  email: yup.string().required(Labels.EMAIL_REQUIRED).email(),
  orcid: yup.string().matches(orcidRegExp, "Orcid not valid (xxxx-xxxx-xxxx-xxxx)"),
  emailRepeat: yup
    .mixed()
    .test("match", Labels.EMAIL_DO_NOT_MATCH, function (email) {
      return email === this.parent.email;
    })
    .required(Labels.EMAIL_REPEAT_REQUIRED),
  password: yup.string().required(Labels.PASSWORD_REQUIRED),
  passwordRepeat: yup
    .mixed()
    .test("match", Labels.PASSWORDS_DO_NOT_MATCH, function (password) {
      return password === this.parent.password;
    })
    .required(Labels.PASSWORD_REPEAT_REQUIRED),
});

export default function Profile() {
  const classes = useStyles();

  const history = useHistory();

  const { Labels } = LabelsContext();

  const { editUser, user, countryList }: UserServiceType = UserService();

  return (
    <>
      <CssBaseline />
      <div className={classes.layout}>
        <Paper className={classes.paper}>
          <Typography component="h1" variant="h4" align="center">
            {Labels.PROFILE}
          </Typography>
          {user && countryList && (
            <Formik initialValues={user} validationSchema={validationSchema} onSubmit={(values) => editUser(values)}>
              {({ handleBlur, values, touched, errors, handleChange, handleSubmit, setFieldValue }) => (
                <Form>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="firstName"
                        name="firstName"
                        label={Labels.FIRSTNAME}
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.firstName && Boolean(errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="middleName"
                        name="middleName"
                        label={Labels.MIDDLENAME}
                        value={values.middleName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.middleName && Boolean(errors.middleName)}
                        helperText={touched.middleName && errors.middleName}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="lastName"
                        name="lastName"
                        label={Labels.LASTNAME}
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.lastName && Boolean(errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                        fullWidth
                        required
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="orcid"
                        name="orcid"
                        placeholder={Labels.ORCID_MASK}
                        label={Labels.ORCID}
                        value={values.orcid}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.orcid && Boolean(errors.orcid)}
                        helperText={touched.orcid && errors.orcid}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="title"
                        name="title"
                        label={Labels.TITLE}
                        value={values.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.title && Boolean(errors.title)}
                        helperText={touched.title && errors.title}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="department"
                        name="department"
                        label={Labels.DEPARTMENT}
                        value={values.department}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.department && Boolean(errors.department)}
                        helperText={touched.department && errors.department}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="division"
                        name="division"
                        label={Labels.DIVISION}
                        value={values.division}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.division && Boolean(errors.division)}
                        helperText={touched.division && errors.division}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="institute"
                        name="institute"
                        label={Labels.INSTITUTE}
                        value={values.institute}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.institute && Boolean(errors.institute)}
                        helperText={touched.institute && errors.institute}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={countryList}
                        value={countryList.find((x: any) => x.countryName === values.country)}
                        onChange={(event, newValue) => {
                          setFieldValue("country", newValue.countryName);
                        }}
                        getOptionLabel={(option: any) => option.countryName}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            margin="normal"
                            id="country"
                            name="country"
                            onBlur={handleBlur}
                            label={Labels.COUNTRY}
                            error={touched.institute && Boolean(errors.institute)}
                            helperText={touched.institute && errors.institute}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="city"
                        name="city"
                        label={Labels.CITY}
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.city && Boolean(errors.city)}
                        helperText={touched.city && errors.city}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="streetName"
                        name="streetName"
                        label={Labels.STREET_NAME}
                        value={values.streetName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.streetName && Boolean(errors.streetName)}
                        helperText={touched.streetName && errors.streetName}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="postalCode"
                        name="postalCode"
                        label={Labels.POSTAL_CODE}
                        value={values.postalCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.postalCode && Boolean(errors.postalCode)}
                        helperText={touched.postalCode && errors.postalCode}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <MuiPhoneNumber
                        variant="outlined"
                        margin="normal"
                        id="phoneNumber"
                        name="phoneNumber"
                        label={Labels.PHONE_NUMBER}
                        value={values.phoneNumber}
                        defaultCountry={"nl"}
                        onChange={(e) => setFieldValue("phoneNumber", e)}
                        onBlur={handleBlur}
                        error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                        helperText={touched.phoneNumber && errors.phoneNumber}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="email"
                        name="email"
                        label={Labels.EMAIL}
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="emailRepeat"
                        name="emailRepeat"
                        label={Labels.REPEAT_EMAIL_ADDRESS}
                        value={values.emailRepeat}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.emailRepeat && Boolean(errors.emailRepeat)}
                        helperText={touched.emailRepeat && errors.emailRepeat}
                        fullWidth
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Password
                        label={Labels.PASSWORD}
                        name="password"
                        statePassword={values.password}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        touchedPassword={touched.password}
                        errorsPassword={errors.password}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Password
                        label={Labels.REPEAT_PASSWORD}
                        name="passwordRepeat"
                        statePassword={values.passwordRepeat}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        touchedPassword={touched.passwordRepeat}
                        errorsPassword={errors.passwordRepeat}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        variant="outlined"
                        margin="normal"
                        id="areasOfExpertise"
                        name="areasOfExpertise"
                        label={Labels.AREAS_OF_EXPERTISE}
                        value={values.areasOfExpertise}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.areasOfExpertise && Boolean(errors.areasOfExpertise)}
                        helperText={touched.areasOfExpertise && errors.areasOfExpertise}
                        fullWidth
                        multiline
                        rows={5}
                      />
                    </Grid>
                  </Grid>
                  <div className={classes.buttons}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={(e) => history.push("/")}
                      className={classes.button}
                    >
                      {Labels.CANCEL}
                    </Button>
                    <Button variant="contained" color="primary" className={classes.button} type="submit">
                      {Labels.SAVE}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </Paper>
      </div>
    </>
  );
}
